import Appsettings from "@/config/appsettings.json";
import { useStore } from "@/store";
import { AuthActionTypes } from "@/store/auth/actions";
import axios, {
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse
} from "axios";
import { error, info } from "./LoggerService";

export class ApiService {
  private static instance: ApiService;
  private api: AxiosInstance;
  /**
   *
   */
  private constructor() {
    const baseURL: string = Appsettings.ApiHost;
    this.api = axios.create({
      baseURL,
    });
  }

  public static get Instance(): ApiService {
    if (this.instance == null) {
      this.instance = new this();
    }
    return this.instance;
  }

  //#region Customer Endpoints

  public async signUp(input: SignUpInput): Promise<boolean> {
    try {
      input.email = input.email.toLocaleLowerCase();
      await this.post(signupEndpoint, null, input);
      return true;
    } catch (e) {
      error('Error in signUp()')
      error(e);
      throw e;
    }
  }

  public async confirmAccount(): Promise<boolean> {
    try {
      await this.patch(confirmAccountEndpoint, null, null);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in confirmAccount()`);
    }
  }

  async updateBarrelName(input: UpdateBarrelNameInput): Promise<void> {
    try {
      const routeData = { logicalBarrelId: input.logicalBarrelId };
      delete input.logicalBarrelId;
      return <void>await this.patch<void>(updateBarrelNameEndpoint, routeData, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in updateBarrelName()`);
    }
  }

  public async getBarrelOptions(): Promise<GetBarrelOptionsOutput> {
    try {
      return await this.get<GetBarrelOptionsOutput>(getBarrelOptionsEndpoint, null);
    } catch (e) {
      error(e);
      throw new Error(`Error in getBarrelOptions()`);
    }
  }

  async pybUserUploadDetail(id: string): Promise<UploadDetailOutput> {
    try {
      return await this.get(PYBUserUploadEndpoint, id);
    } catch (err) {
      error(err);
      throw new Error(`Error in validateDocumentUploadUser()`);
    }
  }

  async retrievePromoCodes(): Promise<RetrievePromotionalCodesOutput> {
    try {
      return await this.get(retrievePromoCodesEndpoint, null);
    } catch (err) {
      error(err);
      throw new Error(`Error in retrievePromoCodes()`);
    }
  }

  async getPromoCode(input: GetPromoCodeInput): Promise<GetPromoCodeOutput> {
    try {
      return await this.get(getPromoCodeEndpoint, input);
    } catch (err) {
      error(err);
      throw new Error(`Error in getPromoCode()`);
    }
  }

  async verifyPromoCode(input: VerifyPromoCodeInput): Promise<VerifyPromoCodeOutput> {
    try {
      return await this.get(verifyPromoCodeEndpoint, input);
    } catch (err) {
      error(err);
      throw new Error(`Error in verifyPromoCode()`);
    }
  }

  async verifyCustomerPromoCode(input: VerifyPromoCodeInput): Promise<VerifyPromoCodeOutput> {
    try {
      return await this.get(verifyCustomerPromoCodeEndpoint, input);
    } catch (err) {
      error(err);
      throw new Error(`Error in verifyPromoCode()`);
    }
  }

  public async validateDocumentUploadUser(
    input: DocumentUploadUserVerification
  ): Promise<UploadDetailOutput> {
    try {
      return await this.post(DocumentUploadEndpoint, null, input);
    } catch (err) {
      error(`Error in validateDocumentUploadUser()`);
      error(err);
      throw err;
    }
  }

  public async saveBarrelSelection(
    input: SaveBarrelSelectionInput
  ): Promise<boolean> {
    try {
      if(Appsettings.DisableWarehouse){
        throw new Error("Shipping barrels will resume on July 29, 2024");
      }
      await this.post<SaveBarrelSelectionInput>(
        saveBarrelSelectionEndpoint,
        null,
        input
      );
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in orderBarrel()`);
    }
  }

  public async orderBarrel(
    input: OrderBarrelInput
  ): Promise<OrderBarrelOutput> {
    try {
      return await this.post<OrderBarrelOutput>(
        orderBarrelEndpoint,
        null,
        input
      );
    } catch (e) {
      error(`Error in orderBarrel()`);
      error(e);
      throw new Error("Shipping barrels will resume on July 1, 2024");
      //throw e;
    }
  }

  async addPromoCode(input: AddPromoCodeInput): Promise<void> {
    try {
      return await this.post(
        addPromoCodeEndpoint,
        null,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in addPromoCode()`);
    }
  }

  async salesReport(input: SalesReportInput): Promise<void> {
    try {
      return await this.post(
        salesReportEndpoint,
        null,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in salesReport()`)
    }
  }

  async promoCodeReport(input: PromoCodeReportInput): Promise<void> {
    try {
      return await this.post(
        promoCodeReportEndpoint,
        null,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in promoCodeReport()`)
    }
  }

  async updatePromoCode(input: UpdatePromoCodeInput): Promise<void> {
    try {
      return await this.put(
        updatePromoCodeEndpoint,
        null,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in updatePromoCode()`);
    }
  }

  async retrieveDeliveryCosts(): Promise<RetrieveDeliveryCostsOutput> {
    try {
      return await this.get(retrieveDeliveryCostsEndpoint, null);
    } catch (err) {
      error(err);
      throw new Error(`Error in retrieveDeliveryCosts()`);
    }
  }

  async updateDeliveryCost(input: UpdateDeliveryCostInput): Promise<void> {
    try {
      return await this.put(
        updateDeliveryCostEndpoint,
        null,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in updateDeliveryCost()`);
    }
  }

  public async barrelDetails(
    input: BarrelDetailsInput
  ): Promise<BarrelDetailsOutput> {
    try {
      return await this.get<BarrelDetailsOutput>(BarrelDetailsEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in barrelDetails()`);
    }
  }

  public async GetBarrels(): Promise<Array<GetBarrelsOutput>> {
    try {
      return await this.get<Array<GetBarrelsOutput>>(GetBarrelsEndpoint, null);
    } catch (e) {
      error(e);
      throw new Error(`Error in getBarrels()`);
    }
  }

  public async barrelByStatus(
    input: BarrelByStatusInput
  ): Promise<BarrelByStatusOutput> {
    try {
      return await this.get<BarrelByStatusOutput>(
        barrelByStatusEndpoint,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in barrelByStatus()`);
    }
  }

  public async getBarrelTransaction(
    input: getTransactionInput
  ): Promise<GetTransactionOutput> {
    try {
      return await this.get<GetTransactionOutput>(
        getTransactionEndpoint,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getBarrelTransaction()`);
    }
  }

  public async retryOrderBarrelPayment(
    input: RetryOrderBarrelPaymentInput
  ): Promise<RetryOrderBarrelPaymentOutput> {
    try {
      const routeData = { barrelId: input.barrelId };
      return <RetryOrderBarrelPaymentOutput>await this.patch(RetryOrderBarrelPaymentEndpoint, routeData, null);
    } catch (e) {
      error(e);
      throw new Error(`Error in retryOrderBarrelPayment()`);
    }
  }

  public async getShippingTransaction(
    input: getShippingTransactionInput
  ): Promise<GetShippingTransactionOutput> {
    try {
      return await this.get<GetShippingTransactionOutput>(
        getShippingTransactionEndpoint,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getShippingTransaction()`);
    }
  }

  public async getCustomsTransaction(
    input: getCustomsTransactionInput
  ): Promise<GetCustomsTransactionOutput> {
    try {
      return await this.get<GetCustomsTransactionOutput>(
        getCustomsTransactionEndpoint,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getCustomsTransaction()`);
    }
  }

  public async getUpgradeTransaction(
    input: getUpgradeTransactionInput
  ): Promise<GetUpgradeTransactionOutput> {
    try {
      return await this.get<GetUpgradeTransactionOutput>(
        getUpgradeTransactionEndpoint,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getUpgradeTransaction()`);
    }
  }

  public async getAllBarrelStatus(): Promise<Array<BarrelStatusOutput>> {
    try {
      return await this.get<Array<BarrelStatusOutput>>(
        getAllStatusEndpoint,
        null
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getAllBarrelStatus()`);
    }
  }

  public async getAllBarrelUpgrades(): Promise<Array<BarrelUpgradeOutput>> {
    try {
      return await this.get<Array<BarrelUpgradeOutput>>(
          getAllUpgradesEndpoint,
          null
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getAllBarrelUpgrades()`);
    }
  }

  async getBarrelUploadInfo(id: string): Promise<UploadInfo> {
    try {
      return await this.get<UploadInfo>(getBarrelUploadInfoEndpoint, id);
    } catch (err) {
      error(err);
      throw new Error(`Error in getBarrelUploadInfo()`);
    }
  }

  public async setBarrelStatus(input: SetBarrelStatusInput, returnFailedRequest?: boolean): Promise<{ success: boolean, errorMessage: string } | boolean> {
    try {
      const { barrelId, status, body } = input;
      const routeData = { barrelId, status };
      const response = await this.patch<any>(setBarrelStatusEndpoint, routeData, body, returnFailedRequest);
      if(!returnFailedRequest){
        return true;
      }

      return response;
    } catch (e) {
      error(e);
      throw new Error(`Error in setBarrelStatus()`);
    }
  }

  public async setBarrelFullness(input: BarrelFullnessInput): Promise<boolean> {
    try {
      const routeData = { barrelId: input.barrelId };
      delete input.barrelId;
      await this.patch(setBarrelFullnessEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in setBarrelFullness()`);
    }
  }

  public async upgradeBarrelSize(input: BarrelUpgradeInput): Promise<boolean> {
    try{
      const routeData = { barrelId: input.barrelId, barrelSize: input.barrelSize };
      await this.post(upgradeBarrelSizeEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in upgradeBarrelSize`)
    }
  }

  public async addItemToBarrel(input: AddItemInput): Promise<boolean> {
    try {
      const routeData = { logicalBarrelId: input.logicalBarrelId };
      delete input.logicalBarrelId;
      delete input.pybNumber;
      await this.post(addItemEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in addItemToBarrel()`);
    }
  }

  public async markItemPacked(
    input: PackBarrelInput
  ): Promise<PackBarrelResult> {
    try {
      const routeData = { BarrelId: input.BarrelId };
      delete input.BarrelId;
      return <PackBarrelResult>await this.patch<PackBarrelResult>(
        setItemPackEndpoint,
        routeData,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in markItemPacked()`);
    }
  }

  public async getAllCustomers(input?: GetAllCustomersInput): Promise<GetAllCustomersOutput> {
    try {
      return await this.get(getAllCustomersEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in getAllCustomers()`);
    }
  }

  public async getEmails(input?: GetEmailsInput): Promise<GetEmailsOutput> {
    try {
      return await this.get(getEmailsEndpoint, input);
    } catch (e) {
      error(e);
      throw e;
    }
  }


  public async getCustomer(input?: GetCustomerInput): Promise<GetCustomerOutput> {
    try {
      return await this.get(getCustomerEndpoint, input);
    } catch (e) {
      error(e);
      throw e;
    }
  }

  public async getEmail(input: GetEmailInput): Promise<GetEmailOutput> {
    try {
      return await this.get(getEmailEndpoint, input);
    } catch (e) {
      error(e);
      throw e;
    }
  }

  public async getAllWorkers(input?: GetAllWorkersInput): Promise<GetAllWorkersOutput> {
    try {
      return await this.get(getAllWorkersEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in getAllWorkers()`);
    }
  }

  public async updateWorker(input?: UpdateWorkerInput): Promise<boolean> {
    try {
      await this.patch(updateWorkerEndpoint, input, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in updateWorker()`);
    }
  }

  public async deleteWorker(input?: DeleteWorkerInput): Promise<boolean> {
    try {
      await this.delete(deleteWorkerEndpoint, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in deleteWorker()`);
    }
  }

  public async customerHomepage(): Promise<customerHomePageOutput> {
    try {
      return await this.get(customerHomepageEndpoint, null);
    } catch (e) {
      error(e);
      throw new Error(`Error in customerHomepage()`);
    }
  }

  public async getBarrelDocuments(id: string): Promise<BarrelDocumentsOutput> {
    try {
      return await this.get(getBarrelDocumentsEndpoint, id);
    } catch (err) {
      error(err);
      throw new Error("Error in getBarrelDocuments()");
    }
  }

  public async getDocumentDownloadLink(input: DocumentDownloadLinkInput): Promise<string> {
    try {
      return await this.get(getDocumentDownloadLinkEndpoint, input);
    } catch (err) {
      error(err);
      throw new Error("Error in getDocumentDownloadLink()");
    }
  }

  public async requestDocumentReupload(input: DocumentReuploadInput) {
    try {
      const routeData = { id: input.id, documentName: input.documentName };
      delete input.id;
      delete input.documentName;
      await this.patch(requestDocumentReuploadEndpoint, routeData, input);
    } catch (err) {
      error(err);
      throw new Error("Error in requestDocumentReupload()");
    }
  }

  public async getNotifications(
    input = "all"
  ): Promise<Array<GetNotificationsOutput>> {
    try {
      return await this.get<Array<GetNotificationsOutput>>(
        getNotificationsEndpoint,
        input
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getNotifications()`);
    }
  }

  public async markNotificationSeen(input: string): Promise<boolean> {
    try {
      const routeData = input;
      await this.patch(markNotificationSeenEndpoint, routeData, null);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in getNotifications()`);
    }
  }

  public async getBarrelExpirationConfig(): Promise<RetrieveBarrelExpirationConfigOutput> {
    try {
      return await this.get<RetrieveBarrelExpirationConfigOutput>(
        retrieveBarrelExpirationConfigEndpoint,
        null
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getBarrelExpirationConfig()`);
    }
  }

  public async updateBarrelExpirationConfig(input: UpdateBarrelExpirationConfigInput): Promise<boolean> {
    try {
      await this.patch(updateBarrelExpirationConfigEndpoint, input, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in updateBarrelExpirationConfig()`);
    }
  }

  public async getItemCategories(): Promise<Array<getItemCategoriesOutput>> {
    try {
      return await this.get<Array<getItemCategoriesOutput>>(
        itemCategoriesEndpoint,
        null
      );
    } catch (e) {
      error(e);
      throw new Error(`Error in getItemCategories()`);
    }
  }

  public async addItemCategories(input: Array<string>): Promise<boolean> {
    try {
      await this.post(itemCategoriesEndpoint, null, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in addItemCategories()`);
    }
  }

  public async editItemCategories(
    input: Array<editItemCategoriesInput>
  ): Promise<boolean> {
    try {
      await this.patch(itemCategoriesEndpoint, null, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in editItemCategories()`);
    }
  }

  public async deleteItemCategories(input: string): Promise<boolean> {
    try {
      return await this.delete(deleteItemCategoryEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in deleteItemCategories()`);
    }
  }

  public async deleteCustomer(input: DeleteCustomerInput): Promise<boolean> {
    try {
      return await this.delete(deleteCustomerEndpoint, input);
    } catch (e) {
      error(e);
      throw e;
    }
  }

  public async removeEmailSubscriber(input: RemoveEmailSubscriberInput): Promise<boolean> {
    try {
      return await this.delete(removeEmailSubscriberEndpoint, input);
    } catch (e) {
      error(e);
      throw e;
    }
  }

  public async deletePromoCode(input: DeletePromoCodeInput): Promise<boolean> {
    try {
      return await this.delete(deletePromoCodeEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in deletePromoCode()`);
    }
  }

  public async deleteItem(input: DeleteItemInput): Promise<boolean> {
    try {
      return await this.delete(deleteItemEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in deleteItem()`);
    }
  }

  public async getPhysicalBarrels(): Promise<Array<string>> {
    try {
      return await this.get<Array<string>>(getPhysicalBarrelsEndpoint, null);
    } catch (e) {
      error(e);
      throw new Error(`Error in getPhysicalBarrels()`);
    }
  }

  public async createPhysicalBarrel(input: Array<string>): Promise<boolean> {
    try {
      return await this.post(createPhysicalBarrelEndpoint, null, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in createPhysicalBarrel()`);
    }
  }

  public async addToPhysicalBarrel(
    input: addToPhysicalBarrelInput
  ): Promise<boolean> {
    try {
      const routeData = { physicalBarrelId: input.physicalBarrelId };
      delete input.physicalBarrelId;
      return <boolean>await this.patch(addToPhysicalBarrelEndpoint, routeData, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in addToPhysicalBarrel()`);
    }
  }

  public async getAllCustomerItems(): Promise<UnpackedItemsOutput> {
    try {
      return await this.get(getAllCustomerItemsEndpoint, null);
    } catch (e) {
      error(e);
      throw new Error(`Error in getAllCustomerItems()`);
    }
  }

  public async getCustomerItems(input: string): Promise<CustomerItemsOutput> {
    try {
      return await this.get(getCustomerItemsEndpoint, input);
    } catch (e) {
      error(e);
      throw new Error(`Error in getCustomerItems()`);
    }
  }
  public async updateCustomer(
    input: UpdateCustomerInput
  ): Promise<boolean> {
    try {
      await this.patch(setUpdateCustomerEndpoint, input, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in updateCustomer()`);
    }
  }
  public async setCustomerItemOption(
    input: CustomerItemOptionInput
  ): Promise<boolean> {
    try {
      const routeData = { itemId: input.itemId };
      delete input.itemId;
      await this.patch(setCustomerItemOptionEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in setCustomerItemOption()`);
    }
  }
  public async confirmCustomerItemOption(
    input: CustomerItemOptionInput
  ): Promise<boolean> {
    try {
      const routeData = input;
      await this.patch(confirmCustomerItemOptionEndpoint, routeData, null);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in confirmCustomerItemOption()`);
    }
  }
  public async addCustomerItems(input: AddItemInput): Promise<boolean> {
    try {
      const routeData = { pybNumber: input.pybNumber };
      delete input.pybNumber;
      await this.post(addCustomerItemEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in addCustomerItems()`);
    }
  }

  public async addEmailSubscriber(input: AddEmailSubscriberInput): Promise<boolean> {
    try {
      const routeData = { emailId: input.emailId };
      delete input.emailId;
      await this.post(addEmailSubscriberEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw e;
    }
  }

  public async resendWelcomeEmail(input: ResendWelcomeEmailInput): Promise<boolean> {
    try {
      await this.patch(resendWelcomeEmailEndpoint, input, {})
      return true;
    } catch (e) {
      error(e);
      throw e;
    }
  }


  public async createUser(input: createUserInput): Promise<boolean> {
    try {
      input.email = input.email.toLocaleLowerCase();
      const routeData = { role: input.role };
      delete input.role;
      await this.post(createUserEndpoint, routeData, input);
      return true;
    } catch (e) {
      error(e);
      throw new Error(`Error in createUser()`);
    }
  }

  public async sendContactForm(nameString: string, emailString: string, messageString: string): Promise<any> {
    try {
      const headers: ContactFormRequest = {
        name: nameString,
        email: emailString,
        message: messageString
      }
      await this.post(contactFormEndpoint, null, headers);
      return true
    } catch (e) {
      error(e)
      throw new Error('Error in sendContactForm()')
    }
  }

  public async cancelBarrel(barrelId: string): Promise<boolean> {
    try {
      await this.patch(cancelBarrelEndpoint, barrelId, {});
      return true
    } catch (e) {
      error(e)
      throw new Error('Error in cancelBarrel()')
    }
  }

  private async get<T>(endpoint: Endpoint<any>, input: any): Promise<T> {
    try {
      let headers: AxiosRequestHeaders = {};
      if (endpoint.token) {
        headers = await ApiService.generateTokenHeader();
      }

      if (!!endpoint.headers && !!input?.headers) {
        headers = { ...headers, ...input.headers }
      }

      const response = await this.api.get<T>(endpoint.path(input), {
        headers,
      });

      if (!ApiService.isSuccessfulResponse(response)) {
        ApiService.logFailedResponse(response);
        throw new Error("Request Failed");
      }

      return response.data;
    } catch (e) {
      error(e);
      throw new Error(`Error sending get request ${JSON.stringify(endpoint)}`);
    }
  }

  private async post<T>(
    endpoint: Endpoint<any>,
    routeData: any,
    input: any
  ): Promise<T> {
    try {
      let headers: AxiosRequestHeaders = {};
      if (endpoint.token) {
        headers = await ApiService.generateTokenHeader();
      }
      const response = await this.api.post<T>(endpoint.path(routeData), input, {
        headers,
      });

      if (!ApiService.isSuccessfulResponse(response)) {
        ApiService.logFailedResponse(response);
        throw new Error("Request Failed");
      }

      return response.data;
    } catch (e) {
      error(e);
      error(`Error sending post request ${JSON.stringify(
        endpoint
      )}, with input ${JSON.stringify(input)}`);
      throw e;
    }
  }

  private async patch<T>(
    endpoint: Endpoint<any>,
    routeData: any,
    input: any,
    returnFailedRequest?: boolean
  ): Promise<T> {
    try {
      let headers: AxiosRequestHeaders = {};
      if (endpoint.token) {
        headers = await ApiService.generateTokenHeader();
      }
      const response = await this.api.patch<T>(
        endpoint.path(routeData),
        input,
        {
          headers,
          validateStatus: function (status) {
            return (status >= 200 && status < 300) || (!!returnFailedRequest && status == 400);
          },
        }
      );

      if (!ApiService.isSuccessfulResponse(response)) {
        ApiService.logFailedResponse(response);
        if(returnFailedRequest){
          return response.data;
        }
        throw new Error("Request Failed");
      }

      return response.data;
    } catch (e) {
      error(e);
      throw new Error(
        `Error sending patch request ${JSON.stringify(
          endpoint
        )}, with input ${JSON.stringify(input)}`
      );
    }
  }

  private async put<T>(
    endpoint: Endpoint<any>,
    routeData: any,
    input: any
  ): Promise<T> {
    try {
      let headers: AxiosRequestHeaders = {};
      if (endpoint.token) {
        headers = await ApiService.generateTokenHeader();
      }
      const response = await this.api.put<T>(
        endpoint.path(routeData),
        input,
        {
          headers,
        }
      );

      if (!ApiService.isSuccessfulResponse(response)) {
        ApiService.logFailedResponse(response);
        throw new Error("Request Failed");
      }

      return response.data;
    } catch (e) {
      error(e);
      throw new Error(
        `Error sending put request ${JSON.stringify(
          endpoint
        )}, with input ${JSON.stringify(input)}`
      );
    }
  }

  private async delete(endpoint: Endpoint<any>, input: any): Promise<boolean> {
    try {
      let headers: AxiosRequestHeaders = {};
      if (endpoint.token) {
        headers = await ApiService.generateTokenHeader();
      }
      const response = await this.api.delete(endpoint.path(input), {
        headers,
      });

      if (!ApiService.isSuccessfulResponse(response)) {
        ApiService.logFailedResponse(response);
        throw new Error("Request Failed");
      }

      return true;
    } catch (e) {
      error(e);
      throw new Error(
        `Error sending patch request ${JSON.stringify(
          endpoint
        )}, with input ${JSON.stringify(input)}`
      );
    }
  }

  private static isSuccessfulResponse(
    response: AxiosResponse<unknown, any>
  ): boolean {
    const lowEndAccept = 200;
    const highEndAccept = 299;

    return lowEndAccept <= response.status && response.status <= highEndAccept;
  }

  private static logFailedResponse(response: AxiosResponse<any>) {
    info("Response Status: " + response.status);
    const headers = response.headers;
    info("Response Headers: " + JSON.stringify(headers));
    const data = response.data;
    info("Response Data:" + JSON.stringify(data));
  }

  private static async generateTokenHeader(): Promise<AxiosRequestHeaders> {
    const store = useStore();
    const token: string = await store.dispatch(
      AuthActionTypes.GetIdToken,
      undefined
    );
    return {
      Authorization: token,
    };
  }
}

interface Endpoint<T> {
  path: (input: T) => string;
  method: "get" | "post" | "patch" | "delete" | "put";
  pathParameters: boolean;
  body: boolean;
  payload: boolean;
  token: boolean;
  headers?: boolean;
}

//#region Sign Up
export type SignUpInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  kin: string;
  pasword: string;
};

export const signupEndpoint: Endpoint<SignUpInput> = {
  path: () => "/customer",
  method: "post",
  pathParameters: false,
  body: true,
  payload: false,
  token: false,
};

//#endregion

//#region Confirm Account

export const confirmAccountEndpoint: Endpoint<any> = {
  path: () => "/confirmaccount",
  method: "patch",
  pathParameters: false,
  body: false,
  payload: false,
  token: true
};


//#endregion

//#region update customer

export type UpdateCustomerInput = {
  pybNumber: string,
  firstName?: string,
  lastName?: string,
  phone?: string,
  kin?: string,
  additionalFields: Record<string, any>
};

export const setUpdateCustomerEndpoint: Endpoint<any> = {
  path: (input: UpdateCustomerInput) => `/customer/${input.pybNumber}`,
  method: "patch",
  pathParameters: true,
  body: true,
  payload: false,
  token: true
};

//#endregion

export type UpdateBarrelNameInput = {
  logicalBarrelId?: string
  name: string
}

export const updateBarrelNameEndpoint: Endpoint<UpdateBarrelNameInput> = {
  path: (input: UpdateBarrelNameInput) => `/barrel/${input.logicalBarrelId}/name`,
  method: "patch",
  pathParameters: false,
  body: false,
  payload: true,
  token: true,
}

//#region GetBarrelOptions

export type BarrelOptions = {
  name: string;
  size: string;
  barrelCost: number;
  shippingCost: number;
  imageUrl: string;
  description: string;
};

export type DeliveryCostMap = Record<string, number>;

export type GetBarrelOptionsOutput = {
  barrelOptions: [BarrelOptions],
  clearanceCost: number;
  pickupCost: number;
  defaultDeliveryCost: number;
  deliveryCostMap: DeliveryCostMap;
};

export const getBarrelOptionsEndpoint: Endpoint<any> = {
  path: () => "/barrel/options",
  method: "get",
  pathParameters: false,
  body: false,
  payload: false,
  token: false,
};

//#endregion

//#region OrderBarrel

export enum PaymentOption {
  UPFRONT = 'UPFRONT',
  STAGES = 'STAGES'
}

export type OrderBarrelInput = {
  barrelHash?: string;
  barrelSize: string;
  capacity: string;
  deliveryMethod: string;
  removeSecondaryPackaging: boolean;
  removeRetailPackaging: boolean;
  deliveryAddress: AddressInput;
  consignee: ConsigneeInput;
  pickupLocation?: string;
  promoCode?: string;
  paymentOption: PaymentOption;
};

export type ConsigneeInput = {
  firstName: string;
  lastName: string;
  email: string;
  trn: string;
  contactNumber: string;
  address: AddressInput;
};

export type AddressInput = {
  line1: string;
  line2: string;
  town: string;
  parish: string;
};

export type OrderBarrelOutput = {
  paynowproRedirectUrl: string;
};

export const orderBarrelEndpoint: Endpoint<OrderBarrelInput> = {
  path: () => "/barrel",
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type RetryOrderBarrelPaymentInput = {
  barrelId: string;
};

export type RetryOrderBarrelPaymentOutput = {
  paynowproRedirectUrl: string;
};

export const RetryOrderBarrelPaymentEndpoint: Endpoint<RetryOrderBarrelPaymentInput> =
{
  path: (input: RetryOrderBarrelPaymentInput) =>
    `/barrel/${input.barrelId}/makeorderbarrelpayment`,
  method: "patch",
  pathParameters: true,
  payload: true,
  token: true,
  body: false,
};
//#endregion

//#region SaveBarrelSelection


export type SaveBarrelSelectionInput = Omit<OrderBarrelInput, "consignee"> & {
  consignee?: ConsigneeInput;
}

export const saveBarrelSelectionEndpoint: Endpoint<SaveBarrelSelectionInput> = {
  path: () => "/barrel/selection",
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: true
};

//#endregion

//#region BarrelDetails

export type GetBarrelsOutput = {
  logicalBarrelId: string;
  barrelNum: string
  pybNumber: string;
  customerName: string;
  fullness: string;
  capacity: string;
  size: string;
  status: string;
  date: string;
  isOpen: boolean;
  isActive: boolean;
};

export type BarrelDetailsInput = {
  logicalBarrelId: string;
};

export const GetBarrelsEndpoint: Endpoint<BarrelDetailsInput> = {
  path: () => `/barrels`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type DocumentUploadUserVerification = {
  logicalBarrelId: string;
  trn: string;
  firstName: string
  lastName: string
};

export type UploadDetailOutput = {
  instruction: string;
  details: DocUploadDetails[];
};

type DocUploadDetails = {
  documentName: string;
  buttonLabel: string;
  acceptedMediaFileType: AcceptedMediaFileType[];
  presignedInfo: PresignedInfo;
  downloadLink?: string;
};

enum AcceptedMediaFileType {
  "pdf" = "application/pdf",
  "jpeg" = "image/jpeg",
  "png" = "image/png",
}

type PresignedInfo = {
  url: string;
  fields: object;
};

export const PYBUserUploadEndpoint: Endpoint<string> = {
  path: (id: string) => `barrel/${id}/documentUpload`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
}

export const DocumentUploadEndpoint: Endpoint<DocumentUploadUserVerification> =
{
  path: (input: DocumentUploadUserVerification) => "/documentUpload",
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: false,
};

export type BarrelDetailsOutput = {
  customerName: string;
  customerPhoneNumber: string;
  barrel: BarrelSummary;
  statusHistory: SimpleBarrelStatusOutput[];
  content: BarrelItemOutput[];
  items: BarrelItemOutput[];
  payments: PaymentOutput[];
  outstandingPayments: OutstandingPayment[];
  upgrades: BarrelUpgrade[];
};

export type BarrelUpgrade = {
  NewSize: string;
  InitialSize: string;
  Date: string;
}

export type SimpleBarrelStatusOutput = {
  BarrelStatus: string;
  StatusDate: string;
};

export type BarrelItemOutput = {
  Items: Array<ItemDetailsOutput>;
  TrackingNumber: string;
  Courier: string;
  ItemId: string;
  ItemName: string;
  Description: string;
  ItemQuantity: number;
  Date: string;
};

export type ItemDetailsOutput = {
  ItemId: string;
  ItemName: string;
  Description: string;
  ItemQuantity: number;
  Date: string;
  Permitted: boolean;
  ProhibitReason: string;
  Packed: boolean
};

export type PaymentOutput = {
  Amount: number;
  Date: string;
  PaymentType: string;
  Success: boolean;
  PaymentId: string;
};

export type OutstandingPayment = {
  Name: string,
  Amount: number,
  PaymentLink: string
}

export const BarrelDetailsEndpoint: Endpoint<BarrelDetailsInput> = {
  path: (input: BarrelDetailsInput) => `/barrel/${input.logicalBarrelId}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};



//#endregion

//#region BarrelStatus
export const getAllStatusEndpoint: Endpoint<any> = {
  path: () => `/barrel/status`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type BarrelStatusOutput = {
  label: string;
  path: string;
  next: Array<string>;
  previous: Array<string>;
};

export type BarrelByStatusInput = {
  status: string;
};

export type BarrelByStatusOutput = {
  Cost: number;
  BarrelName: string;
  StatusDate: string;
  LogicalBarrelId: string;
  PYBNumber: string;
  BarrelCapacity: string;
  BarrelFullness: string;
};

export const barrelByStatusEndpoint: Endpoint<BarrelByStatusInput> = {
  path: (input: BarrelByStatusInput) => `/barrel/status/${input.status}`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type SetBarrelStatusInput = {
  status: string;
  barrelId: string;
  body?: { [key: string]: any }
};

export const setBarrelStatusEndpoint: Endpoint<SetBarrelStatusInput> = {
  path: (input: SetBarrelStatusInput) =>
    `/barrel/${input.barrelId}/${input.status}`,
  method: "patch",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};
//#endregion

//#region DocumentUpload
export type UploadInfo = {
  Path: string
}

export const getBarrelUploadInfoEndpoint: Endpoint<string> = {
  path: (id: string) => `/barrel/${id}/pass`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
}

export type DocumentReuploadInput = {
  id?: string
  documentName?: string
  user: string
}

export const requestDocumentReuploadEndpoint: Endpoint<DocumentReuploadInput> =
{
  path: (input: any) =>
    `/barrel/${input.id}/document/${input.documentName}/reUploadRequest`,
  method: "patch",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type DocumentDownloadLinkInput = {
  id: string;
  documentName: string;
};

export const getDocumentDownloadLinkEndpoint: Endpoint<DocumentDownloadLinkInput> = {
  path: (input: DocumentDownloadLinkInput) => `/barrel/${input.id}/document/${input.documentName}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
}

export type BarrelDocumentsOutput = {
  barrelActive: boolean
  documents: Document[]
}
type Document = {
  Name: string
  Collected: boolean
  ReuploadRequested: boolean
}
export const getBarrelDocumentsEndpoint: Endpoint<string> = {
  path: (id: string) => `/barrel/${id}/documents`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
}
//#endregion

//#region Items
export type AddItemInput = {
  trackingNumber: string;
  courier: string;
  items: AddItemDetails[];
  isBarrelFullnessUpdated: boolean;
  barrelFullness: string;
  logicalBarrelId?: string;
  pybNumber?: string;
};

export type AddItemDetails = {
  itemName: string;
  description: string;
  quantity: number;
  permitted: boolean;
  prohibitReason: string;
};

export const addItemEndpoint: Endpoint<AddItemInput> = {
  path: (input: AddItemInput) => `/barrel/${input.logicalBarrelId}/items`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type DeleteItemInput = {
  itemId: string;
  logicalBarrelId: string;
};

export const deleteItemEndpoint: Endpoint<DeleteItemInput> = {
  path: (input: DeleteItemInput) =>
    `/barrel/${input.logicalBarrelId}/items/${input.itemId}`,
  method: "delete",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region barrel expiration config

export type RetrieveBarrelExpirationConfigOutput = {
  expirationNumber: ExpirationConfigData,
  expiringNumber: ExpirationConfigData
}

export type ExpirationConfigData = {
  days: number;
  description: string;
}

export enum ExpirationSK {
  ExpirationNumber = "ExpirationNumber",
  ExpiringNumber = "ExpiringNumber"
}

export type UpdateBarrelExpirationConfigInput = {
  sk: ExpirationSK;
  days: number;
}

export const retrieveBarrelExpirationConfigEndpoint: Endpoint<any> = {
  path: () =>
    `/barrel/expiration/config`,
  method: "get",
  pathParameters: false,
  body: false,
  payload: true,
  token: true
};


export const updateBarrelExpirationConfigEndpoint: Endpoint<UpdateBarrelExpirationConfigInput> = {
  path: (input: UpdateBarrelExpirationConfigInput) =>
    `/barrel/expiration/config/${input.sk}`,
  method: "patch",
  pathParameters: true,
  body: true,
  payload: false,
  token: true
};

//#endregion

//#region Promotional Codes

export interface PromoCode {
  id: string,
  code: string,
  uses: number,
  description?: string,
  discount?: PromoCodeDiscount,
  singleUse: boolean,
  firstPayment: boolean,
  endDate?: string
}



export interface PromoCodeDiscount {
  name: string,
  amount: number,
  type: PromoCodeDiscountType
}

export enum PromoCodeDiscountType {
  Fixed = "Fixed",
  Percentage = "Percentage"
}

export type RetrievePromotionalCodesOutput = PromoCode[];


export type GetPromoCodeInput = {
  code: string;
}

export type GetPromoCodeOutput = PromoCode;


export type VerifyPromoCodeInput = GetPromoCodeInput;

export type VerifyPromoCodeOutput = {
  isSuccessful: boolean,
  discount?: PromoCodeDiscount,
  reason?: string
}

export type DeletePromoCodeInput = {
  id: string
}

export type AddPromoCodeInput = Omit<PromoCode, "id" | "uses">;

export type UpdatePromoCodeInput = Omit<PromoCode, "uses">;

export type PromoCodeReportInput = {
  promoCodeIds: string[],
  shouldRetrieveAll: boolean,
  startDate: string,
  endDate: string
};

export const retrievePromoCodesEndpoint: Endpoint<any> = {
  path: () =>
    `/promocode`,
  method: "get",
  pathParameters: false,
  body: false,
  payload: true,
  token: true
};

export const getPromoCodeEndpoint: Endpoint<any> = {
  path: (input: GetPromoCodeInput) =>
    `/promocode/${input.code}`,
  method: "get",
  pathParameters: true,
  body: false,
  payload: true,
  token: true
};

export const verifyPromoCodeEndpoint: Endpoint<any> = {
  path: (input: VerifyPromoCodeInput) =>
    `/promocode/verify/${input.code}`,
  method: "get",
  pathParameters: true,
  body: false,
  payload: true,
  token: false
};

export const verifyCustomerPromoCodeEndpoint: Endpoint<any> = {
  path: (input: VerifyPromoCodeInput) =>
    `/promocode/verifycustomer/${input.code}`,
  method: "get",
  pathParameters: true,
  body: false,
  payload: true,
  token: true
};

export const deletePromoCodeEndpoint: Endpoint<any> = {
  path: (input: DeletePromoCodeInput) =>
    `/promocode/${input.id}`,
  method: "delete",
  pathParameters: true,
  body: false,
  payload: false,
  token: true
};

export const addPromoCodeEndpoint: Endpoint<any> = {
  path: () =>
    `/promocode`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: false,
  token: true
};

export const updatePromoCodeEndpoint: Endpoint<any> = {
  path: () =>
    `/promocode`,
  method: "put",
  pathParameters: false,
  body: true,
  payload: false,
  token: true
};

export const promoCodeReportEndpoint: Endpoint<any> = {
  path: () =>
    `/promocode/report`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: false,
  token: true
};

//#endregion

//#region Payments

export type SalesReportInput = {
  startDate: string,
  endDate: string
};


export const salesReportEndpoint: Endpoint<any> = {
  path: () =>
    `/payment/report`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: false,
  token: true
};

//#endregion

//#region Delivery Cost

export type DeliveryCost = {
  id: string,
  parish: string,
  cost: number
}

export type RetrieveDeliveryCostsOutput = DeliveryCost[];


export type UpdateDeliveryCostInput = {
  id: string,
  cost: number
}


export const retrieveDeliveryCostsEndpoint: Endpoint<any> = {
  path: () =>
    `/deliverycost`,
  method: "get",
  pathParameters: false,
  body: false,
  payload: true,
  token: true
};



export const updateDeliveryCostEndpoint: Endpoint<any> = {
  path: () =>
    `/deliverycost`,
  method: "put",
  pathParameters: false,
  body: true,
  payload: false,
  token: true
};

//#endregion

//#region welcome

export type customerHomePageOutput = {
  customerName: string;
  customerPhoneNumber: string;
  pybNumber: string;
  kin?: string;
  barrels: BarrelSummary[];
  customerItemsCount: number;
};

export type BarrelSummary = {
  BarrelName: string;
  BarrelCapacity: string;
  BarrelSize: string;
  BarrelStatus: string;
  LogicalBarrelId: string;
  BarrelClosed: boolean;
  BarrelActive: boolean;
  BarrelFullness: string;
  Date: string;
  Consignee: ConsigneeInput;
  DeliveryMethod: string;
  DeliveryAddress: AddressInput;
  BarrelNum: string,
  RemoveRetailPackaging: boolean,
  ShippingPaymentLink?: string,
  CustomsDutyPaymentLink?: string
};

export const customerHomepageEndpoint: Endpoint<any> = {
  path: () => `/customer/homepage`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region transaction

export type getTransactionInput = {
  transactionId: string;
  barrelId: string;
};

export type GetTransactionOutput = {
  success: boolean;
  barrelSize: string;
  barrelCapacity: string;
  total: number;
  paddedCardNumber: string;
  currencyText: string;
  authorizationNumber: string;
  customerName: string;
  date: string;
  id: string;
  receiptNo: string;
};

export const getTransactionEndpoint: Endpoint<getTransactionInput> = {
  path: (input: getTransactionInput) =>
    `/barrel/${input.barrelId}/orderpayment/${input.transactionId}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region shipping transaction

export type getShippingTransactionInput = getTransactionInput;

export type GetShippingTransactionOutput = GetTransactionOutput;

export const getShippingTransactionEndpoint: Endpoint<getShippingTransactionInput> = {
  path: (input: getShippingTransactionInput) =>
    `/barrel/${input.barrelId}/shipping/${input.transactionId}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region customs transaction

export type getCustomsTransactionInput = getTransactionInput;

export type GetCustomsTransactionOutput = GetTransactionOutput;

export const getCustomsTransactionEndpoint: Endpoint<getCustomsTransactionInput> = {
  path: (input: getCustomsTransactionInput) =>
    `/barrel/${input.barrelId}/customs/${input.transactionId}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region upgrade transaction

export type getUpgradeTransactionInput = getTransactionInput;

export type GetUpgradeTransactionOutput = GetTransactionOutput;

export const getUpgradeTransactionEndpoint: Endpoint<getUpgradeTransactionInput> = {
  path: (input: getUpgradeTransactionInput) =>
    `/barrel/${input.barrelId}/upgrade/${input.transactionId}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion


//#region BarrelFullness

export type BarrelFullnessInput = {
  barrelFullness: string;
  barrelId?: string;
};

export const setBarrelFullnessEndpoint: Endpoint<BarrelFullnessInput> = {
  path: (input: BarrelFullnessInput) =>
    `/barrel/${input.barrelId}/barrel_fullness`,
  method: "patch",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};
//#endregion

//#region categories

export type editItemCategoriesInput = {
  id: string;
  newName: string;
};

export type getItemCategoriesOutput = {
  id: string;
  ICName: string;
};

export const itemCategoriesEndpoint: Endpoint<any> = {
  path: () => `/item_category`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export const deleteItemCategoryEndpoint: Endpoint<any> = {
  path: (input: string) => `/item_category/${input}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region PhysicalBarrel
export const getPhysicalBarrelsEndpoint: Endpoint<Array<string>> = {
  path: () => `/barrels`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type createPhysicalBarrelInput = {
  logicalBarrelIds: string[];
};

export const createPhysicalBarrelEndpoint: Endpoint<Array<string>> = {
  path: () => `/barrel/create`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export type addToPhysicalBarrelInput = {
  logicalBarrelIds: string[];
  physicalBarrelId?: string;
};

export const addToPhysicalBarrelEndpoint: Endpoint<any> = {
  path: (input: addToPhysicalBarrelInput) =>
    `/physicalbarrel/${input.physicalBarrelId}/logicalBarrel`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region create User

export type createUserInput = {
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  company: string
};

export const createUserEndpoint: Endpoint<any> = {
  path: (input: createUserInput) => `/${input.role}`,
  method: "post",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region GetNotifications
export type GetNotificationsOutput = {
  Id: string;
  BarrelId: string;
  Date: string;
  Description: string;
  NotificationType: string;
  Seen: boolean;
  Title: string;
};

export const getNotificationsEndpoint: Endpoint<any> = {
  path: (input: string) => `/customer/notifications?state=${input}`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export const markNotificationSeenEndpoint: Endpoint<any> = {
  path: (input: string) => `/notification/${input}/seen`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region PackBarrel
export type PackBarrelInput = {
  BarrelId?: string;
};

export type PackBarrelResult = {
  failed: Array<string>;
};

export const setItemPackEndpoint: Endpoint<PackBarrelInput> = {
  path: (input: PackBarrelInput) => `/barrel/${input.BarrelId}/pack`,
  method: "patch",
  pathParameters: true,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region customer items

export type CustomerItemOptionInput = {
  pybNumber: string;
  itemId?: string;
};

export type UnpackedItemsOutput = {
  data: Array<CustomerUnpackedItems>;
};

export type CustomerUnpackedItems = {
  pybNumber: string;
  name: string;
  items: Array<UnpackedItem>;
};

export type CustomerItemsOutput = {
  ItemCount: number;
  Items: Array<UnpackedItem>;
};

export type UnpackedItem = {
  ItemId: string;
  ItemName: string;
  ItemQuantity: number;
  Description: string;
  Date: string;
  Permitted: boolean;
  ProhibitReason: string;
  TrackingNumber: string;
  Courier: string;
  Option: string;
  OptionConfirmed: boolean;
};

export type ContactFormRequest = {
  name: string,
  email: string,
  message: string
}

export const contactFormEndpoint: Endpoint<any> = {
  path: () => "/contact_form",
  method: "post",
  pathParameters: false,
  body: true,
  payload: false,
  token: false
};

export const cancelBarrelEndpoint: Endpoint<string> = {
  path: (barrelId: string) => `barrel/${barrelId}/cancel`,
  method: "patch",
  pathParameters: false,
  body: false,
  payload: false,
  token: true
}

export const getAllCustomerItemsEndpoint: Endpoint<any> = {
  path: () => `/customers/items`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export const getCustomerItemsEndpoint: Endpoint<any> = {
  path: (input: string) => `/customer/${input}/items`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export const setCustomerItemOptionEndpoint: Endpoint<any> = {
  path: (input: any) => `/customer/item/${input.itemId}/option`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export const addCustomerItemEndpoint: Endpoint<any> = {
  path: (input: any) => `/customer/${input.pybNumber}/item`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

export const confirmCustomerItemOptionEndpoint: Endpoint<any> = {
  path: (input: any) =>
    `/customer/${input.pybNumber}/item/${input.itemId}/confirmoption`,
  method: "get",
  pathParameters: false,
  body: true,
  payload: true,
  token: true,
};

//#endregion

//#region customers

export type GetAllCustomersInput = {
  headers: {
    startKey?: string,
    limit?: number
  }
}

export type GetAllCustomersOutput = {
  data: Array<Customer>;
  lastEvaluatedKey: string
};

export type Customer = {
  pybNumber: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone: string;
  email: string;
  status: string;
  signUpDate: string;
};

export const getAllCustomersEndpoint: Endpoint<any> = {
  path: (input: GetAllCustomersInput) => `/customers`,
  method: "get",
  pathParameters: false,
  headers: true,
  body: true,
  payload: true,
  token: true,
};

//#endregion


//#region get customer

export type GetCustomerInput = {
  pybNumber: string
}

export type GetCustomerOutput = Omit<Customer, "pybNumber">;

export const getCustomerEndpoint: Endpoint<any> = {
  path: (input: GetCustomerInput) => `/customer/${input.pybNumber}`,
  method: "get",
  pathParameters: true,
  headers: false,
  body: false,
  payload: true,
  token: true
};

//#endregion



//#region delete customer

export type DeleteCustomerInput = {
  pybNumber: string
}

export const deleteCustomerEndpoint: Endpoint<any> = {
  path: (input: DeleteCustomerInput) => `/customer/${input.pybNumber}`,
  method: "get",
  pathParameters: true,
  headers: false,
  body: false,
  payload: false,
  token: true
};

//#endregion

//#region Worker

export type GetAllWorkersInput = {
  headers: {
    startKey?: string,
    limit?: number
  }
}

export type GetAllWorkersOutput = {
  data: Array<Worker>
  lastEvaluatedKey: string
};

export type Worker = {
  id: string
  type: string
  firstName: string
  lastName: string
  email: string
  status: string
  company: string
  signUpDate: string
};

export const getAllWorkersEndpoint: Endpoint<any> = {
  path: (input: any) => `/workers`,
  method: "get",
  pathParameters: false,
  headers: true,
  body: true,
  payload: true,
  token: true,
};

export type UpdateWorkerInput = {
  id: string
  type: string
  updates: WorkerUpdateEntry[]
}

export type WorkerUpdateEntry = {
  attribute: string
  value: string
}

export const updateWorkerEndpoint: Endpoint<any> = {
  path: (input: UpdateWorkerInput) => `/worker`,
  method: "patch",
  pathParameters: true,
  body: true,
  payload: false,
  token: true
}

export const deleteWorkerEndpoint: Endpoint<any> = {
  path: (input: DeleteWorkerInput) => `/worker/${input.id}`,
  method: "delete",
  pathParameters: true,
  body: false,
  payload: false,
  token: true
}

export type DeleteWorkerInput = {
  id: string
}

//#endregion



//#region emails

export type GetEmailsInput = {
  headers: {
    startKey?: string,
    limit?: number
  }
}

export type GetEmailsOutput = {
  data: Array<Omit<Email, "subscribers">>;
  lastEvaluatedKey: string
};

export type Email = {
  id: string,
  name: string,
  subscribers: EmailSubscribers[]
};

export const getEmailsEndpoint: Endpoint<any> = {
  path: (input: GetEmailsInput) => `/emails`,
  method: "get",
  pathParameters: false,
  headers: true,
  body: true,
  payload: true,
  token: true,
};

//#endregion



//#region get email

export type GetEmailInput = {
  id: string
}

export type EmailSubscribers = {

}

export type GetEmailOutput = Email;

export const getEmailEndpoint: Endpoint<any> = {
  path: (input: GetEmailInput) => `/email/${input.id}`,
  method: "get",
  pathParameters: true,
  headers: false,
  body: false,
  payload: true,
  token: true
};

//#endregion


//#region remove email subscriber

export type RemoveEmailSubscriberInput = {
  emailId: string,
  subscriberAddress: string
}

export const removeEmailSubscriberEndpoint: Endpoint<any> = {
  path: (input: RemoveEmailSubscriberInput) => `/email/${input.emailId}/subscriber/${input.subscriberAddress}`,
  method: "get",
  pathParameters: true,
  headers: false,
  body: false,
  payload: false,
  token: true
};

//#endregion

//#region add email subscriber

export type AddEmailSubscriberInput = {
  emailId?: string,
  name: string,
  emailAddress: string,
  adminId?: string
}

export const addEmailSubscriberEndpoint: Endpoint<any> = {
  path: (input: AddEmailSubscriberInput) => `/email/${input.emailId}/subscriber`,
  method: "post",
  pathParameters: true,
  headers: false,
  body: true,
  payload: false,
  token: true
};

//#endregion

// #region resend welcome Email

export type ResendWelcomeEmailInput = {
  pybNumber: string
}

export const resendWelcomeEmailEndpoint: Endpoint<any> = {
  path: (input: ResendWelcomeEmailInput) => `/customer/${input.pybNumber}/resendwelcomeemail`,
  method: "patch",
  pathParameters: true,
  headers: false,
  body: false,
  payload: false,
  token: true
}

//#endregion

// #region barrel upgrades

export type BarrelUpgradeInput = {
  barrelId: string,
  barrelSize: string
}

export type BarrelUpgradeOutput = {
  barrelId: string,
  initialSize: string,
  newSize: string,
  date: string
}

export const upgradeBarrelSizeEndpoint: Endpoint<any> = {
  path: (input: BarrelUpgradeInput) => `/barrel/${input.barrelId}/upgrade/${input.barrelSize}`,
  method: "post",
  pathParameters: false,
  headers: false,
  body: true,
  payload: true,
  token: true
}

export const getAllUpgradesEndpoint: Endpoint<any> = {
  path: () => `/barrels/upgrades`,
  method: "get",
  pathParameters: false,
  body: false,
  payload: false,
  token: true,
};


//#endregion
